import React, { useState } from 'react';

// "Not an achievement yet?" — captures intent before the work is done.
// Deliberately minimal: one field, no title/date/impact. See
// design-system/Terminology.md for the copy conventions used here.
export default function NotAnAchievementYetModal({ isOpen, onClose, onSave }) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleClose = () => {
    if (submitting) return;
    setNote('');
    setError(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!note.trim()) {
      setError("Enter what you're working on first.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSave(note.trim());
      setNote('');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <svg className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l2 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-gray-900">Not an achievement yet?</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Jot it down now — you'll turn it into a real achievement once it's done.
              </p>
            </div>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none shrink-0">&times;</button>
          </div>

          <form onSubmit={handleSubmit}>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              What are you working on?
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={1000}
              autoFocus
              disabled={submitting}
              placeholder="e.g. Leading the Q4 billing migration — rolls out next sprint"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 bg-stone-50 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 resize-none transition-colors"
            />

            {error && (
              <p className="mt-2 text-xs text-red-600">{error}</p>
            )}

            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-500 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Saving...' : 'Save placeholder'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
